/**
 * resources.ts
 *
 * Shared connector lifecycle for agent sessions — connect, watch, and build
 * tool injection for all connector types (filesystem-face and tool-face).
 *
 * Used by agent.ts (REPL), factory-assets/connectors/flow/run-flow.ts (flow), and serve.ts (WebSocket server).
 * Extracted from duplicated inline blocks in each of those files.
 */

import {
  buildConnectorPromptSection,
  buildSdkConnectorTools,
  type ConnectorChangeEvent,
  type ConnectorDeclaration,
  ConnectorManager,
  ConnectorStartupError,
  findMissingPackages,
  installNpmPackages,
  loadConnectorDeclarations,
  type PreMintedSecretProvider,
  registerBuiltinConnectors,
  type SecretProvider,
  SecretProviderChain,
  type SyncStatusPayload,
  type TokenMediator,
} from "@skaile/workspaces/connectors";
import {
  type McpServerDeclaration,
  type RuntimeAssetsResult,
  resolveRuntimeAssets,
} from "@skaile/workspaces/core";
import { createLogger } from "@skaile/workspaces/core/logging";
import type { ConnectorStatusEvent } from "@skaile/workspaces/types";
import { WorkspacePlugin } from "@skaile/workspaces/workspace-plugin";
import { z } from "zod";
import type { CapabilityRegistry } from "./capability-registry.js";
import { FileChangedCoalescer } from "./file-changed-coalescer.js";
import {
  ExternalMcpManager,
  type McpReauthorizer,
  resolveExternalMcpDeclarations,
  resolveRecordSecrets,
  type SecretProviderLike,
} from "./external-mcp.js";
import { buildMcpPromptSection } from "./mcp-prompt.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Result of {@link buildAgentResources}.
 *
 * @docLink packages/runner/dev-guide#flow-execution-turn-based-model
 */
export interface AgentResourcesResult {
  /** Connected ConnectorManager instance — null if no connectors declared or package missing. */
  resourceManager: any | null;
  /** Markdown section to append to the system prompt — empty string if no resources. */
  resourcePromptSection: string;
  /**
   * In-process MCP server map to pass as `mcpServers` in `AgentConfig`.
   * Populated only for the `claude-sdk` driver; undefined for all others.
   */
  mcpServers: Record<string, unknown> | undefined;
  /**
   * Resolved runtime assets — catalog entries, implicit refs, npm deps, warnings.
   * Held by the session so live-update paths (add_resource, configure)
   * can call `runtimeAssets.refresh()` and consume the same shared view.
   * `null` only when @skaile/workspaces/connectors or skaile.yaml parsing failed.
   */
  runtimeAssets: RuntimeAssetsResult | null;
  /** Disconnect all connectors, unmount all mounts, and stop watchers. */
  dispose: () => Promise<void>;
  /**
   * Start filesystem and connector watchers. Call AFTER the transport is
   * listening — watching can be slow on large directory trees and must not
   * block session startup.
   */
  startWatching: () => void;
  /**
   * Lazily construct (once) and return the session's external MCP manager —
   * the live target for `runner.add_mcp_server`. Reassigns the same local that
   * `dispose()` reads, so a runtime-created manager is torn down on teardown.
   * Serve mode only (requires a capability registry); throws otherwise.
   */
  getOrCreateExternalMcpManager: () => ExternalMcpManager;
  /** The external MCP manager if constructed (boot or lazy), else null. */
  getExternalMcpManager: () => ExternalMcpManager | null;
  /**
   * Markdown section for the agent system prompt: an inventory of the
   * connected MCP servers plus per-server guidance built from the materialized
   * MCP.md bodies. Empty string when there is neither.
   */
  mcpPromptSection: string;
}

/**
 * Callback hooks for filesystem and connector lifecycle events.
 *
 * @docLink packages/runner/dev-guide#flow-execution-turn-based-model
 */
export interface ResourceWatchCallbacks {
  /**
   * Called by mountable adapters to report incremental sync progress.
   * Used by serve.ts to forward sync_status events to the frontend.
   */
  onSyncStatus?: (connectorId: string, status: SyncStatusPayload) => void;
  /**
   * Called when a watched connector detects an in-process change.
   * Used by serve.ts to forward file_changed events to the frontend.
   * Change events are deduplicated within `dedupTtlMs` per path.
   */
  onFileChanged?: (connectorId: string, event: ConnectorChangeEvent) => void;
  /** Dedup window for file_changed events in milliseconds. Default: 500. */
  dedupTtlMs?: number;
  /**
   * Called whenever a connector's connection state changes
   * (`connecting` → `connected` | `error`, plus `disconnected`). Used by
   * serve.ts to forward `connector_status` events to the platform — the
   * channel deferred (background) filesystem mounts report progress through.
   */
  onConnectorStatus?: (event: ConnectorStatusEvent) => void;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Set up all connectors declared in `skaile.yaml` for an agent session.
 *
 * Steps:
 *   1. Register built-in connectors
 *   2. Load connector declarations from skaile.yaml
 *   3. Connect all connectors via ConnectorManager (filesystem + tool faces)
 *   4. Optionally start filesystem/connector watchers (serve.ts use case)
 *   5. Build the system prompt section (mount paths + connector tools)
 *   6. Build in-process MCP server (claude-sdk only)
 *
 * @param projectDir       Workspace root — where skaile.yaml lives
 * @param driverType       Active bridge driver ID: "omp" | "claude-sdk"
 * @param onLog            Optional log callback; silently ignored if omitted
 * @param watch            Optional watch callbacks; watching is skipped if omitted
 * @param secretProvider   Optional secret provider chain for connector field
 *                         resolution and `env:NAME` lookups
 * @param sessionMeta      Provider/model/sessionId metadata used for logging
 * @param tokenMediator    Optional callback for `auth: backend` connectors to
 *                         mint short-lived tokens. Threaded into `ConnectorManager`.
 * @param preMintedSecrets Optional v3 pre-minted credential provider — when
 *                         supplied, `auth: backend` connectors find their initial
 *                         token in-process instead of paying a
 *                         `request_access_token` round-trip at connect time.
 * @param deferFilesystemMounts When true, rclone-backed filesystem-face
 *                         connectors (marked `deferMount`) are brought up in the
 *                         background instead of being awaited — so a slow remote
 *                         can't gate session bootstrap. Their mount path is still
 *                         rendered immediately; progress rides `onConnectorStatus`.
 * @param capabilityRegistry When supplied, external (declarative / recipe-backed)
 *                         MCP servers are spawned and connected BY THE RUNNER and
 *                         their tools registered into this registry as
 *                         `mcp`-origin capabilities — the bridge-agnostic path
 *                         that works on every driver. When omitted, the legacy
 *                         claude-sdk-only path is used (external stdio servers are
 *                         delegated to the SDK's `mcpServers` query option).
 * @param fallbackConnectorDeclarations Declarations used ONLY when the disk
 *                         (`skaile.yaml` + materialized assets) yields zero
 *                         connector declarations. Serve mode maps the
 *                         `session_init` resolvedConfig here so broker-mode
 *                         sessions (no yaml on disk) still get their resources.
 *                         Disk-first: any disk declaration disables the fallback.
 * @param extraMcpDeclarations MCP servers attached at runtime
 *                         (`runner.attach_instance`) that `skaile.yaml` does not
 *                         declare. Serve mode replays its live-attach registry
 *                         here so a session rebuild respawns them instead of
 *                         dropping them as undeclared "ghosts". Merged into the
 *                         disk set before spawn; a disk declaration of the same
 *                         id wins.
 * @docLink packages/runner/dev-guide#flow-execution-turn-based-model
 */
export async function buildAgentResources(
  projectDir: string,
  driverType: string,
  _onLog?: (line: string) => void,
  watch?: ResourceWatchCallbacks,
  secretProvider?: unknown,
  sessionMeta?: { provider?: string; model?: string; sessionId?: string; managedCodex?: boolean },
  tokenMediator?: TokenMediator,
  preMintedSecrets?: PreMintedSecretProvider,
  deferFilesystemMounts?: boolean,
  capabilityRegistry?: CapabilityRegistry,
  fallbackConnectorDeclarations?: ConnectorDeclaration[],
  extraMcpDeclarations?: McpServerDeclaration[],
): Promise<AgentResourcesResult> {
  // Project declarations can execute host processes or expand host secret refs. Capture
  // the admitted empty set before any resource effects; recreates repeat this check.
  const managedProjectMcp = sessionMeta?.managedCodex
    ? await resolveExternalMcpDeclarations(projectDir, sessionMeta.sessionId, true)
    : undefined;
  const resLog = createLogger({
    kind: "runner",
    subkind: "resources",
    instance: sessionMeta?.sessionId ?? "no-session",
  });
  // `_onLog` is preserved in the public signature for back-compat with external
  // callers, but is unused internally — structured emission via `resLog` and the
  // per-source loggers below reaches the caller through the OnLogBridgeSink that
  // the runner bootstrap installs at the LogStore level.
  const noopResult: AgentResourcesResult = {
    resourceManager: null,
    resourcePromptSection: "",
    mcpServers: undefined,
    runtimeAssets: null,
    dispose: async () => {},
    startWatching: () => {},
    getOrCreateExternalMcpManager: () => {
      throw new Error("external MCP manager unavailable — no capability registry (CLI/flow mode)");
    },
    getExternalMcpManager: () => null,
    mcpPromptSection: "",
  };

  try {
    registerBuiltinConnectors();

    // Resolve catalog entries + implicit refs from skaile.yaml + factory-assets repo.
    // Held on the session result so live-update paths can call refresh().
    const runtimeAssets = await resolveRuntimeAssets(projectDir);
    for (const w of runtimeAssets.warnings) {
      resLog.warn("runtime asset warning", { code: w.code, message: w.message });
    }

    // Optional: install missing npm peer deps before mountAll/connectAll runs.
    if (!sessionMeta?.managedCodex)
      await maybeInstallNpmPeerDeps(runtimeAssets, projectDir, resLog);

    // --- Mounts ---
    // --- Connectors (filesystem + tool faces) ---
    // The unified ConnectorManager owns the full lifecycle for every connector
    // declared in skaile.yaml — mountable (filesystem-face) and tool-face alike.
    // Disk-first: skaile.yaml + materialized assets win outright. The wire
    // fallback engages ONLY when disk yields zero declarations (broker mode
    // binds just the workspace subdir — no yaml file exists there).
    const diskDeclarations = loadConnectorDeclarations(projectDir);
    const connectorDeclarations =
      diskDeclarations.length > 0 ? diskDeclarations : (fallbackConnectorDeclarations ?? []);
    if (diskDeclarations.length === 0 && connectorDeclarations.length > 0) {
      resLog.info("no disk connector declarations — using session_init resolvedConfig fallback", {
        count: connectorDeclarations.length,
      });
    }
    let resourceManager: ConnectorManager | null = null;

    if (connectorDeclarations.length > 0) {
      resourceManager = await connectAllConnectors(
        connectorDeclarations,
        projectDir,
        runtimeAssets,
        secretProvider,
        tokenMediator,
        preMintedSecrets,
        deferFilesystemMounts,
        watch,
        resLog,
      );
    }

    // Build the system prompt section (mount paths + connector tools).
    const resourcePromptSection = resourceManager
      ? buildConnectorPromptSection(resourceManager)
      : "";

    // ── MCP servers ─────────────────────────────────────────────────────────
    let mcpServers: Record<string, unknown> | undefined;
    let workspacePluginToShutdown: { shutdown(): Promise<void> } | null = null;
    let externalMcpManager: ExternalMcpManager | null = null;
    // Lazily resolved when we actually need MCP declarations (serve mode or
    // claude-sdk). Stays undefined for other drivers without a capability
    // registry so we do not emit unexpected mcp:* log entries.
    let externalMcpDeclarations:
      | import("@skaile/workspaces/core").McpServerDeclaration[]
      | undefined;

    // The runtime add path stages `mcp:<id>` bearers on `preMintedSecrets`; boot
    // decls resolve `env:`/`forge:` refs via `secretProvider`. Chain both so one
    // manager serves both — prefixed refs route to the owning provider, so adding
    // preMintedSecrets is harmless for boot. Avoid a chain with undefined members.
    const mcpSecretChain: unknown = preMintedSecrets
      ? new SecretProviderChain(
          [preMintedSecrets, secretProvider as SecretProvider | undefined].filter(
            Boolean,
          ) as SecretProvider[],
        )
      : secretProvider;

    const getOrCreateExternalMcpManager = (): ExternalMcpManager => {
      if (!capabilityRegistry) {
        throw new Error(
          "external MCP manager unavailable — no capability registry (CLI/flow mode)",
        );
      }
      if (!externalMcpManager) {
        externalMcpManager = createExternalMcpManager(
          capabilityRegistry,
          mcpSecretChain,
          sessionMeta,
        );
      }
      return externalMcpManager;
    };

    // In-process MCP servers (connector tools + workspace plugin) stay on the
    // claude-sdk `mcpServers` path: they are SDK server *instances*, not external
    // subprocesses, so the bridge-agnostic capability path below does not apply
    // to them.
    if (driverType === "claude-sdk") {
      const inProcess = await buildInProcessMcpServers(
        resourceManager,
        projectDir,
        driverType,
        sessionMeta,
      );
      mcpServers = inProcess.mcpServers;
      workspacePluginToShutdown = inProcess.workspacePluginToShutdown;
    }

    // ── External (declarative / recipe-backed) MCP servers ──────────────────
    // e.g. excel / ppt — `mcp_servers:` entries from skaile.yaml, including
    // recipe-backed ones. When a capability registry is available (serve mode),
    // the RUNNER spawns these subprocesses itself and registers each tool as an
    // `mcp`-origin capability — the bridge-agnostic surface every driver
    // consumes, with no dependence on the claude-agent-sdk `mcpServers` option.
    // Without a registry (CLI / flow), fall back to the legacy claude-sdk-only
    // delegation where the SDK spawns the stdio subprocess.
    if (capabilityRegistry) {
      // Live-attached servers merge in here (before spawn and before the prompt
      // section below) — the disk set alone would drop them as undeclared ghosts.
      // Only this branch can receive them: a live attach needs the registry.
      externalMcpDeclarations = mergeExtraMcpDeclarations(
        managedProjectMcp ??
          (await resolveExternalMcpDeclarations(projectDir, sessionMeta?.sessionId)),
        extraMcpDeclarations,
      );
      externalMcpManager = await startExternalMcpServers(
        capabilityRegistry,
        externalMcpDeclarations,
        mcpSecretChain,
        sessionMeta,
      );
    } else if (driverType === "claude-sdk") {
      externalMcpDeclarations = await resolveExternalMcpDeclarations(
        projectDir,
        sessionMeta?.sessionId,
      );
      mcpServers = wireExternalMcpServersForSdk(
        mcpServers,
        externalMcpDeclarations,
        secretProvider,
        sessionMeta,
      );
    }

    // If neither connectors nor workspace-plugin nor runner-managed MCP servers
    // produced anything useful for a non-claude-sdk driver, return noop to avoid
    // overhead. A connected external MCP manager is "something useful" — fall
    // through so its dispose() (which kills the subprocesses) is wired up.
    // Skip this short-circuit in serve mode (capabilityRegistry present): the
    // real result carries getOrCreateExternalMcpManager so hot-plug adds work.
    if (
      !resourceManager &&
      driverType !== "claude-sdk" &&
      !externalMcpManager?.hasServers() &&
      !capabilityRegistry
    )
      return noopResult;

    // Deferred watch setup — called by the consumer after the transport is listening
    // so that slow filesystem scans (e.g. large node_modules over Docker bind mounts)
    // don't block session startup.
    let coalescer: FileChangedCoalescer | null = null;
    const startWatching = () => {
      if (resourceManager && watch?.onFileChanged) {
        const dedupTtlMs = watch.dedupTtlMs ?? 500;
        const recentlyEmitted = new Map<string, number>();

        // Sits after per-path dedup: bulk git ops touch many distinct paths so dedup
        // doesn't fire, but the coalescer collapses by directory into one rep each.
        coalescer = new FileChangedCoalescer(
          (mountId, event) => watch.onFileChanged!(mountId, event),
          undefined,
          Date.now,
          (m) => resLog.warn(m),
        );

        resourceManager.watchAll((connectorId: string, event: ConnectorChangeEvent) => {
          const dedupKey = `${connectorId}:${event.path}`;
          const now = Date.now();
          const lastEmit = recentlyEmitted.get(dedupKey);
          if (lastEmit && now - lastEmit < dedupTtlMs) return;
          recentlyEmitted.set(dedupKey, now);

          if (recentlyEmitted.size > 200) {
            for (const [key, ts] of recentlyEmitted) {
              if (now - ts > dedupTtlMs) recentlyEmitted.delete(key);
            }
          }

          coalescer!.push(connectorId, event);
        });
      }
    };

    const dispose = async () => {
      coalescer?.dispose();
      // Kill runner-spawned MCP subprocesses + deregister their capabilities.
      try {
        await externalMcpManager?.dispose();
      } catch {
        /* best-effort */
      }
      if (workspacePluginToShutdown) {
        try {
          await workspacePluginToShutdown.shutdown();
        } catch {
          /* best-effort */
        }
      }
      try {
        await resourceManager?.disconnectAll();
      } catch {
        /* best-effort */
      }
    };

    const mcpPromptSection = buildMcpPromptSection(
      externalMcpDeclarations ?? [],
      externalMcpManager?.listServers(),
    );

    return {
      resourceManager,
      resourcePromptSection,
      mcpServers,
      runtimeAssets,
      dispose,
      startWatching,
      getOrCreateExternalMcpManager,
      getExternalMcpManager: () => externalMcpManager,
      mcpPromptSection,
    };
  } catch {
    // @skaile/workspaces/connectors not installed or skaile.yaml parse error — resources disabled
    return noopResult;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type ResourcesLogger = ReturnType<typeof createLogger>;

/**
 * Install missing npm peer deps before mountAll/connectAll runs.
 *
 * Disabled by default for serve mode (no network or write access in
 * production agent containers); enable via SKAILE_AUTO_INSTALL_DEPS=true.
 * Asset-manager.install() is the canonical preflight install path.
 */
async function maybeInstallNpmPeerDeps(
  runtimeAssets: RuntimeAssetsResult,
  projectDir: string,
  resLog: ResourcesLogger,
): Promise<void> {
  if (process.env.SKAILE_AUTO_INSTALL_DEPS === "true") {
    const required = runtimeAssets.requiredNpmDeps.required;
    if (required.length > 0) {
      const missing = findMissingPackages(required, projectDir);
      if (missing.length > 0) {
        resLog.info("installing missing npm peer deps", { packages: missing });
        const r = await installNpmPackages(missing, projectDir);
        if (!r.success) resLog.error("npm install failed", undefined, { output: r.output });
      }
    }
  }
}

/** Log the per-connector status carried on a connect report. */
function logConnectorReport(
  results: Array<{ connected: boolean; id: string; mountPath?: string; error?: unknown }>,
  resLog: ResourcesLogger,
): void {
  for (const r of results) {
    if (r.connected) {
      resLog.info("connector ok", { id: r.id, mountPath: r.mountPath });
    } else {
      resLog.error("connector failed", undefined, { id: r.id, error: r.error });
    }
  }
}

/**
 * Create the unified ConnectorManager and connect every declared connector
 * (filesystem + tool faces). The manager owns the full lifecycle.
 */
async function connectAllConnectors(
  connectorDeclarations: Parameters<ConnectorManager["connectAll"]>[0],
  projectDir: string,
  runtimeAssets: RuntimeAssetsResult,
  secretProvider: unknown,
  tokenMediator: TokenMediator | undefined,
  preMintedSecrets: PreMintedSecretProvider | undefined,
  deferFilesystemMounts: boolean | undefined,
  watch: ResourceWatchCallbacks | undefined,
  resLog: ResourcesLogger,
): Promise<ConnectorManager> {
  const resourceManager = new ConnectorManager(projectDir, {
    secrets: secretProvider as any,
    catalogEntries: runtimeAssets.catalogEntries,
    tokenMediator,
    preMintedSecrets,
    onStatusChange: watch?.onConnectorStatus,
  });
  // failOnReadWriteError: failed rw-mounts leave the agent workspace silently empty; deferred mounts degrade via status events instead.
  try {
    const report = await resourceManager.connectAll(connectorDeclarations, {
      failOnReadWriteError: true,
      deferMounts: deferFilesystemMounts,
    });
    logConnectorReport(report.results, resLog);
  } catch (err) {
    if (err instanceof ConnectorStartupError) {
      // Serve mode forwards ConnectorManager's connector_status error events,
      // while the runner keeps wiring unrelated resources.
      logConnectorReport(err.report.results, resLog);
      return resourceManager;
    }
    throw err;
  }
  return resourceManager;
}

/**
 * Build the in-process MCP servers (connector tools + workspace plugin) for the
 * claude-sdk `mcpServers` path. They are SDK server *instances*, not external
 * subprocesses, so the bridge-agnostic capability path does not apply to them.
 */
async function buildInProcessMcpServers(
  resourceManager: ConnectorManager | null,
  projectDir: string,
  driverType: string,
  sessionMeta: { provider?: string; model?: string; sessionId?: string } | undefined,
): Promise<{
  mcpServers: Record<string, unknown> | undefined;
  workspacePluginToShutdown: { shutdown(): Promise<void> } | null;
}> {
  let mcpServers: Record<string, unknown> | undefined;

  // Connector tools (only if connectors exist)
  if (resourceManager) {
    const connectorsLog = createLogger({ kind: "mcp", subkind: "skaile-connectors" });
    try {
      const server = await buildSdkConnectorTools(resourceManager, { z });
      if (server) {
        mcpServers = { "skaile-connectors": server };
        connectorsLog.info("server constructed", {
          connectorCount: resourceManager.listConnectors().length,
        });
      } else {
        connectorsLog.debug("server skipped (no tools to expose)");
      }
    } catch (err) {
      connectorsLog.error("server construction failed", err);
      throw err;
    }
  }

  // Workspace plugin — always inject for claude-sdk.
  // `onLog` is intentionally NOT passed: structured emission via
  // `WorkspacePlugin.log` already reaches the caller's `onLog` through the
  // OnLogBridgeSink installed by the runner bootstrap. Passing `onLog` here
  // would cause double-emit for plugin lifecycle events (booted, warning).
  const workspacePlugin = new WorkspacePlugin({
    projectDir,
    connectorManager: resourceManager ?? undefined,
    driver: driverType,
    provider: sessionMeta?.provider,
    model: sessionMeta?.model,
  });
  const wsServer = await workspacePlugin.buildWorkspaceMcpServer();
  if (wsServer) {
    mcpServers = { ...(mcpServers ?? {}), "skaile-workspace": wsServer };
  }
  return { mcpServers, workspacePluginToShutdown: workspacePlugin };
}

/**
 * Fold the live-attach registry into the disk-resolved declarations. Disk wins
 * on an id collision: once `skaile.yaml` catches up with a live attach it is the
 * canonical copy, and only it has been through recipe / `${workspace}` resolution.
 */
function mergeExtraMcpDeclarations(
  diskDecls: McpServerDeclaration[],
  extra: McpServerDeclaration[] | undefined,
): McpServerDeclaration[] {
  if (!extra?.length) return diskDecls;
  const declared = new Set(diskDecls.map((d) => d.id));
  return [...diskDecls, ...extra.filter((d) => !declared.has(d.id))];
}

/**
 * Spawn external (declarative / recipe-backed) MCP servers and register each
 * tool as an `mcp`-origin capability — the bridge-agnostic surface every driver
 * consumes. Returns the manager (with running subprocesses), or null when no
 * external declarations were resolved.
 */
async function startExternalMcpServers(
  capabilityRegistry: CapabilityRegistry,
  resolvedDecls: import("@skaile/workspaces/core").McpServerDeclaration[],
  secretProvider: unknown,
  sessionMeta: { provider?: string; model?: string; sessionId?: string } | undefined,
): Promise<ExternalMcpManager | null> {
  if (resolvedDecls.length === 0) return null;
  const mgr = createExternalMcpManager(capabilityRegistry, secretProvider, sessionMeta);
  await mgr.start(resolvedDecls);
  return mgr;
}

/**
 * Construct (do not start) an `ExternalMcpManager` with the 401 reauthorizer
 * wired. Shared by the boot path ({@link startExternalMcpServers}) and the lazy
 * runtime path (`runner.add_mcp_server`).
 */
function createExternalMcpManager(
  capabilityRegistry: CapabilityRegistry,
  secretProvider: unknown,
  sessionMeta: { provider?: string; model?: string; sessionId?: string } | undefined,
): ExternalMcpManager {
  return new ExternalMcpManager(
    capabilityRegistry,
    secretProvider as SecretProviderLike | undefined,
    sessionMeta?.sessionId,
    undefined,
    buildMcpReauthorizer(capabilityRegistry, secretProvider, sessionMeta?.sessionId),
  );
}

/** `MCP__<sanitized id>__AUTH` — cross-repo contract: must mirror the platform's bearer-provisioning key. */
export function mcpAuthSecretKey(id: string): string {
  return `MCP__${id.replace(/[^A-Za-z0-9_]/g, "_")}__AUTH`;
}

/**
 * Build the 401 re-mint callback for `auth: backend` remote MCP servers: ask the
 * platform mediator for a fresh bearer (`host.refresh_credential`), then rewrite
 * the provisioned `MCP__<id>__AUTH` secret in place so the manager's reconnect
 * picks it up. Returns `undefined` when the secret store is immutable (CLI mode)
 * — without a writable store there is nothing to rewrite, so no re-mint applies.
 */
function buildMcpReauthorizer(
  capabilityRegistry: CapabilityRegistry,
  secretProvider: unknown,
  sessionId: string | undefined,
): McpReauthorizer | undefined {
  const provider = secretProvider as
    | { provision?: (s: Record<string, string>) => void }
    | undefined;
  const provision = provider?.provision?.bind(provider);
  if (!provision) return undefined;
  const log = createLogger({ kind: "mcp", subkind: "reauth", instance: sessionId ?? "no-session" });
  return async (decl) => {
    if (!decl.providerLinkId || decl.auth !== "backend") return false;
    let mint: { ok?: boolean; token?: string };
    try {
      // No `rejection`: the field is scoped to the `ai-credentials` input, and
      // this is `mcp-server`. Not "we could not classify" — a 401 here is
      // auth-shaped — the field simply is not part of this kind's contract.
      mint = (await capabilityRegistry.invokeRemote("host.refresh_credential", {
        kind: "mcp-server",
        id: decl.providerLinkId,
        reason: "retry-401",
      })) as { ok?: boolean; token?: string };
    } catch (err) {
      log.warn("MCP credential re-mint round-trip failed", {
        id: decl.id,
        error: err instanceof Error ? err.message : String(err),
      });
      return false;
    }
    if (!mint?.ok || !mint.token) return false;
    provision({ [mcpAuthSecretKey(decl.id)]: `Bearer ${mint.token}` });
    return true;
  };
}

/**
 * Legacy claude-sdk-only delegation: translate external MCP declarations into
 * the SDK's `mcpServers` config so the SDK spawns the stdio subprocess itself.
 * Returns the (possibly extended) `mcpServers` map.
 */
function wireExternalMcpServersForSdk(
  mcpServers: Record<string, unknown> | undefined,
  resolvedDecls: import("@skaile/workspaces/core").McpServerDeclaration[],
  secretProvider: unknown,
  sessionMeta: { provider?: string; model?: string; sessionId?: string } | undefined,
): Record<string, unknown> | undefined {
  const mcpLog = createLogger({
    kind: "mcp",
    subkind: "wiring",
    instance: sessionMeta?.sessionId ?? "no-session",
  });
  for (const decl of resolvedDecls) {
    const serverConfig = toSdkMcpServerConfig(
      decl,
      secretProvider as SecretProviderLike | undefined,
    );
    if (serverConfig) {
      mcpServers = { ...(mcpServers ?? {}), [decl.id]: serverConfig };
      createLogger({ kind: "mcp", subkind: decl.id }).info("MCP server registered", {
        transport: decl.transport ?? "stdio",
        ...(decl.transport === "stdio" || !decl.transport
          ? { command: decl.command, args: decl.args }
          : { url: decl.url }),
      });
    } else {
      mcpLog.error("MCP server config invalid (translation returned null)", undefined, {
        id: decl.id,
        transport: decl.transport,
      });
    }
  }
  return mcpServers;
}

/**
 * Convert a `McpServerDeclaration` from `skaile.yaml` into the SDK's
 * `McpServerConfig` shape accepted by `AgentConfig.mcpServers`.
 *
 * Secret references in `env` and `headers` values (e.g. `env:MY_TOKEN`) are
 * resolved through the session's `SecretProvider` chain when available.
 *
 * Returns `null` when the declaration is missing required fields for its
 * transport type (e.g. no `command` for a stdio server).
 */
function toSdkMcpServerConfig(
  decl: McpServerDeclaration,
  secrets?: SecretProviderLike,
): Record<string, unknown> | null {
  const transport = decl.transport ?? "stdio";

  if (transport === "stdio") {
    if (!decl.command) return null;
    const cfg: Record<string, unknown> = { type: "stdio", command: decl.command };
    if (decl.args?.length) cfg.args = decl.args;
    if (decl.env && Object.keys(decl.env).length > 0) {
      cfg.env = resolveRecordSecrets(decl.env, secrets);
    }
    return cfg;
  }

  if (transport === "sse" || transport === "http") {
    if (!decl.url) return null;
    const cfg: Record<string, unknown> = { type: transport, url: decl.url };
    if (decl.headers && Object.keys(decl.headers).length > 0) {
      cfg.headers = resolveRecordSecrets(decl.headers, secrets);
    }
    return cfg;
  }

  return null;
}
